import { useContext } from 'react';
import {
  AuthContext,
  type AuthContextValue,
  type CurrentOperator,
} from './authContextValue';

export type { AuthContextValue, CurrentOperator } from './authContextValue';

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
}

export function useCurrentOperator(): CurrentOperator | null {
  return useAuth().operator;
}

/**
 * The signed-in operator, or `null` — including for a component rendered OUTSIDE an
 * `AuthProvider`, where {@link useCurrentOperator} throws (DNKADM-0402).
 *
 * Reads the context directly for exactly the reason {@link useStaffTier} does: a
 * providerless render must degrade, never throw. `null` here means "no tenant payment
 * context", which the money surfaces already handle — they say so and decline to charge
 * a card, the same as a session whose claims carry no tenant. It never grants anything.
 */
export function useOptionalOperator(): CurrentOperator | null {
  return useContext(AuthContext)?.operator ?? null;
}

/**
 * The ONE staff-tier read the feature screens use (DNKADM-0361) — true only for the
 * staff FLOOR (`operator.isStaffTier`, DNKADM-0359's derivation off the `tenant_role`
 * claim). It exists so no feature re-derives the tier for itself:
 * `roles.includes('STAFF')` scattered through the features is precisely the drift this
 * hook prevents, and one seam is what the next unit builds on.
 *
 * **What it decides, and what it never decides.** It is a RENDER-tier hint carrying the
 * same contract as the flag it reads. Since DNKADM-0375 STAFF is front of house: every
 * RUN capability is open and SET UP is closed, and this hook hides only what dink-api
 * keeps OWNER/ADMIN (SET UP, plus the ADMIN-class money actions), so a STAFF operator
 * never meets a button that fails. Authorization stays the server's — nothing here
 * grants what the server would refuse.
 *
 * Reads the context DIRECTLY rather than through {@link useAuth} so a component rendered
 * outside an `AuthProvider` resolves to `false` instead of throwing. `false` is the
 * pre-0361 rendering — every affordance offered, every read attempted, the server
 * deciding — so the providerless default can only ever be today's behaviour, never a
 * grant. In the shipped app `AuthGate` wraps every routed screen, so a real STAFF session
 * always resolves through the provider.
 */
export function useStaffTier(): boolean {
  return useContext(AuthContext)?.operator?.isStaffTier === true;
}
